import {Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {FilterMenusBase} from '@components/filter-bar/filter-menus-base';
import {Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {MatMenuTrigger} from '@angular/material/menu';
import * as L from 'leaflet';
import {AdService} from '@services/ad.service';
import {AlertService} from '@services/alert.service';
import {TrackingService} from '@services/tracking.service';
import {CommonService} from '@services/common.service';
import {FavoriteMapItem} from '@models/favorite';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'assets/marker-icon-2x.png',
    iconUrl: 'assets/marker-icon.png',
    shadowUrl: 'assets/marker-shadow.png',
});

const LIST_COLORS = [
    '#1a73e8', '#e8710a', '#188038', '#d93025', '#9334e6',
    '#12b5cb', '#c5221f', '#f9ab00', '#1e8e3e', '#5f6368',
];

@Component({
    selector: 'app-favorites-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss']
})
export class MapComponent extends FilterMenusBase implements OnInit, OnChanges, OnDestroy {

    // Mode embarqué (dans la vue Liste, via le switch) : masque l'en-tête/filtres propres
    // et applique les filtres partagés reçus en input.
    @Input() embedded = false;
    @Input() filters: {
        title?: string,
        status?: string[],
        ville?: string[],
        price_min?: number,
        price_max?: number,
        rendement_min?: number,
        rendement_max?: number,
    } = null;
    @Input() currentListId: string = null;

    loading = true;
    items: FavoriteMapItem[] = [];
    listColors: { id: string, name: string, color: string }[] = [];
    hiddenLists = new Set<string>();
    fromList: string = null;

    availableStatuses: string[] = ['analyse', 'contact', 'visite', 'offre', 'offre_reponse', 'offre_ok', 'offre_ko', 'buy', 'sell', 'abandon'];
    selectedStatuses = new Set<string>();

    @ViewChild('listsTrigger') listsTrigger: MatMenuTrigger;
    @ViewChild('statusTrigger') statusTrigger: MatMenuTrigger;

    private map: L.Map;
    private listLayers = new Map<string, L.LayerGroup>();

    constructor(
        private el: ElementRef,
        private router: Router,
        private route: ActivatedRoute,
        private titleService: Title,
        private adService: AdService,
        private alertService: AlertService,
        public trackingService: TrackingService,
        public commonService: CommonService,
    ) {
        super();
    }

    ngOnInit() {
        if (!this.embedded) {
            this.titleService.setTitle('LyBox - Carte de mes favoris');
        }
        this.trackingService.click('favorite_list_map_open', '');
        this.fromList = this.embedded ? this.currentListId : this.route.snapshot.queryParamMap.get('from');
        // Stocké dans sub_items : un rechargement filtré annule la requête initiale
        // encore en vol (sinon sa réponse tardive écraserait les marqueurs filtrés)
        this.sub_items = this.adService.getFavoritesMap(this.buildMapQuery()).subscribe(result => {
            this.items = result || [];
            this.buildListColors();
            if (this.fromList && this.fromList !== 'all' && this.listColors.some(l => l.id === this.fromList)) {
                this.listColors.forEach(l => {
                    if (l.id !== this.fromList) {
                        this.hiddenLists.add(l.id);
                    }
                });
            }
            this.loading = false;
            setTimeout(() => this.initMap(), 100);
        }, () => {
            this.loading = false;
            this.alertService.error('Impossible de charger la carte des favoris');
        });
    }

    // Filtres partagés (mode embarqué) envoyés au backend pour filtrer la carte comme la liste.
    private buildMapQuery() {
        if (!this.embedded) {
            return null;
        }
        const f = this.filters || {};
        return {
            list: this.currentListId,
            title: f.title,
            status: f.status,
            ville: f.ville,
            price_min: f.price_min,
            price_max: f.price_max,
            rendement_min: f.rendement_min,
            rendement_max: f.rendement_max,
        };
    }

    // Rechargements filtrés : une seule requête en vol (sinon une réponse lente
    // peut écraser une plus récente avec des données périmées)
    private sub_items: Subscription;

    private reloadItems() {
        if (this.sub_items) {
            this.sub_items.unsubscribe();
        }
        this.sub_items = this.adService.getFavoritesMap(this.buildMapQuery()).subscribe(result => {
            this.items = result || [];
            this.buildListColors();
            if (this.items.length === 0) {
                // Le conteneur (*ngIf sur items.length) va être détruit : détruire aussi
                // la carte, sinon elle resterait liée à un nœud détaché et le prochain
                // rechargement avec résultats afficherait un conteneur vide
                if (this.map) {
                    this.map.remove();
                    this.map = null;
                }
                return;
            }
            if (!this.map) {
                // Carte jamais initialisée (premier chargement à 0 résultat) ou détruite
                // après un résultat vide -> l'initialiser maintenant que le *ngIf rend le conteneur
                setTimeout(() => { this.initMap(); this.renderMarkers(); }, 100);
                return;
            }
            this.renderMarkers();
        });
    }

    get backLink(): any[] {
        if (this.fromList) {
            return ['/app/favorites', this.fromList];
        }
        return ['/app/favorites'];
    }

    ngOnChanges(changes: SimpleChanges) {
        // Filtres partagés modifiés depuis la vue Liste -> recharger la carte (backend filtré)
        // (pas de garde sur this.map : après un premier chargement à 0 résultat la carte
        // n'existe pas encore, reloadItems sait l'initialiser si des favoris reviennent)
        if (this.embedded && changes.filters && !changes.filters.firstChange) {
            this.reloadItems();
        }
    }

    ngOnDestroy() {
        if (this.sub_items) {
            this.sub_items.unsubscribe();
        }
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    private fitToVisible() {
        const valid = this.items.filter(i => isFinite(i.latitude) && isFinite(i.longitude) && i.latitude !== 0 && i.longitude !== 0);
        const visible = valid.filter(i => !this.hiddenLists.has(i.favorite_list_id) && this.matchesFilters(i));
        const toFit = visible.length > 0 ? visible : valid;
        if (this.map && toFit.length > 0) {
            const bounds = L.latLngBounds(toFit.map(i => [i.latitude, i.longitude] as L.LatLngTuple));
            this.map.fitBounds(bounds, {padding: [40, 40], maxZoom: 14});
        }
    }

    private buildListColors() {
        const seen = new Map<string, string>();
        this.items.forEach(item => {
            if (!seen.has(item.favorite_list_id)) {
                seen.set(item.favorite_list_id, item.favorite_list_name);
            }
        });
        this.listColors = Array.from(seen.entries()).map(([id, name], i) => ({
            id,
            name,
            color: LIST_COLORS[i % LIST_COLORS.length],
        }));
    }

    private colorFor(listId: string): string {
        const found = this.listColors.find(l => l.id === listId);
        return found ? found.color : LIST_COLORS[0];
    }

    private initMap() {
        // L.map() sur un conteneur déjà initialisé lève « Map container is already initialized »
        if (this.map) {
            return;
        }
        const container = this.el.nativeElement.querySelector('.map-container');
        if (!container) {
            return;
        }

        this.map = L.map(container, {
            scrollWheelZoom: true,
            zoomControl: true,
        }).setView([46.5, 2.5], 6);

        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap'
        }).addTo(this.map);

        this.renderMarkers();

        // La carte peut être détruite (résultat vide, ngOnDestroy) avant ce timer.
        const map = this.map;
        setTimeout(() => {
            if (this.map === map) map.invalidateSize();
        }, 200);
    }

    // (Re)construit les couches et les marqueurs à partir de this.items (après un rechargement filtré).
    private renderMarkers() {
        if (!this.map) {
            return;
        }
        this.listLayers.forEach(layer => this.map.removeLayer(layer));
        this.listLayers.clear();

        const valid = this.items.filter(i => isFinite(i.latitude) && isFinite(i.longitude) && i.latitude !== 0 && i.longitude !== 0);

        this.listColors.forEach(l => {
            const layer = L.layerGroup();
            this.listLayers.set(l.id, layer);
            if (!this.hiddenLists.has(l.id)) {
                layer.addTo(this.map);
            }
        });

        valid.filter(i => this.matchesFilters(i)).forEach(item => this.drawItem(item));
        this.fitToVisible();
    }

    private drawItem(item: FavoriteMapItem) {
        const layer = this.listLayers.get(item.favorite_list_id);
        if (!layer) {
            return;
        }
        const latlng: L.LatLngExpression = [item.latitude, item.longitude];
        const color = this.colorFor(item.favorite_list_id);
        const popupHtml = this.buildPopup(item);

        if (item.radius && item.radius > 50) {
            L.circle(latlng, {
                radius: item.radius,
                color,
                fillColor: color,
                fillOpacity: 0.15,
                weight: 2,
            }).addTo(layer).bindPopup(popupHtml);
        }

        L.circleMarker(latlng, {
            radius: 8,
            color: '#fff',
            weight: 2,
            fillColor: color,
            fillOpacity: 1,
        }).addTo(layer).bindPopup(popupHtml);
    }

    toggleList(listId: string) {
        if (!this.map) {
            return;
        }
        const layer = this.listLayers.get(listId);
        if (!layer) {
            return;
        }
        if (this.hiddenLists.has(listId)) {
            this.hiddenLists.delete(listId);
            layer.addTo(this.map);
        } else {
            this.hiddenLists.add(listId);
            this.map.removeLayer(layer);
        }
    }

    isHidden(listId: string): boolean {
        return this.hiddenLists.has(listId);
    }

    showAllLists() {
        if (!this.map || this.hiddenLists.size === 0) {
            return;
        }
        Array.from(this.hiddenLists).forEach(id => {
            const layer = this.listLayers.get(id);
            if (layer) {
                layer.addTo(this.map);
            }
        });
        this.hiddenLists.clear();
    }

    hideAllLists() {
        if (!this.map) {
            return;
        }
        this.listColors.forEach(l => {
            if (!this.hiddenLists.has(l.id)) {
                const layer = this.listLayers.get(l.id);
                if (layer) {
                    this.map.removeLayer(layer);
                }
                this.hiddenLists.add(l.id);
            }
        });
    }

    getListsFilterLabel(): string {
        if (this.hiddenLists.size === 0) {
            return 'Tous';
        }
        const visible = this.listColors.filter(l => !this.hiddenLists.has(l.id));
        if (visible.length === 0) {
            return 'Aucun';
        }
        if (visible.length <= 3) {
            return visible.map(l => l.name).join(', ');
        }
        return `${visible.length}/${this.listColors.length}`;
    }

    toggleStatus(status: string) {
        if (this.selectedStatuses.has(status)) {
            this.selectedStatuses.delete(status);
        } else {
            this.selectedStatuses.add(status);
        }
        this.refreshMarkers();
    }

    isStatusSelected(status: string): boolean {
        return this.selectedStatuses.has(status);
    }

    clearStatusFilter() {
        if (this.selectedStatuses.size === 0) {
            return;
        }
        this.selectedStatuses.clear();
        this.refreshMarkers();
    }

    getStatusFilterLabel(): string {
        if (this.selectedStatuses.size === 0 || this.selectedStatuses.size === this.availableStatuses.length) {
            return 'Tous';
        }
        const labels = Array.from(this.selectedStatuses).map(s => this.commonService.getStatusLib(s));
        const text = labels.join(', ');
        return text.length > 25 ? text.substring(0, 25) + '...' : text;
    }

    // Fermeture des menus de la barre : héritée de FilterMenusBase (seuls la zone
    // « intérieure » et les triggers sont propres à la carte)
    protected filterMenuInsideSelectors = ['.fav-map-filters', '.cdk-overlay-pane'];

    protected filterMenuTriggers(): MatMenuTrigger[] {
        return [this.listsTrigger, this.statusTrigger];
    }

    private matchesFilters(item: FavoriteMapItem): boolean {
        // Mode embarqué : le backend a déjà filtré (title/statut/ville/prix/rendement).
        if (this.embedded) {
            return true;
        }
        // Mode autonome : filtre statut local
        if (this.selectedStatuses.size === 0) {
            return true;
        }
        return this.selectedStatuses.has(item.status || 'analyse');
    }

    getStatusCount(status: string): number {
        return this.items.filter(i => !this.hiddenLists.has(i.favorite_list_id) && (i.status || 'analyse') === status).length;
    }

    private refreshMarkers() {
        if (!this.map) {
            return;
        }
        this.listLayers.forEach(layer => layer.clearLayers());
        const valid = this.items.filter(i => isFinite(i.latitude) && isFinite(i.longitude) && i.latitude !== 0 && i.longitude !== 0);
        valid.filter(i => this.matchesFilters(i)).forEach(item => this.drawItem(item));
    }

    private buildPopup(item: FavoriteMapItem): string {
        const price = item.price > 0 ? `<div class="fav-pop-price">${item.price.toLocaleString('fr-FR')} €</div>` : '';
        const city = [item.zip_code, item.city].filter(v => v && v !== '').join(' ');
        const cityLine = city ? `<div class="fav-pop-city">${this.escape(city)}</div>` : '';
        const listLine = `<div class="fav-pop-list" style="color:${this.colorFor(item.favorite_list_id)}">${this.escape(item.favorite_list_name)}</div>`;
        const title = item.title ? `<div class="fav-pop-title">${this.escape(item.title)}</div>` : '';
        const openUrl = `/app/favorites/${item.favorite_list_id}/${item.id}`;
        const openBtn = `<a class="fav-pop-open" href="${openUrl}">Ouvrir</a>`;
        return `<div class="fav-pop">${listLine}${title}${price}${cityLine}${openBtn}</div>`;
    }

    private escape(value: string): string {
        if (!value) {
            return '';
        }
        return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
}
